import { useState, useEffect, useCallback } from 'react';
import { Map, List, Users, DollarSign, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { socket } from '../chat/socket';
import DateSelector from './DateSelector';
import ExpressMapTab from './ExpressMapTab';
import ExpressListTab from './ExpressListTab';
import ExpressDriversTab from './ExpressDriversTab';
import ExpressPricingTab from './ExpressPricingTab';

const API_URL = process.env.REACT_APP_API_URL;

const TABS = [
  { id: 'map',     label: 'Mapa',        Icon: Map },
  { id: 'list',    label: 'Entregas',    Icon: List },
  { id: 'drivers', label: 'Conductores', Icon: Users },
  { id: 'pricing', label: 'Precios',     Icon: DollarSign },
];

function getTodayLocal() {
  const now   = new Date();
  const year  = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day   = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function ExpressDashboard({ user_id, setActiveComponent }) {
  const [activeTab, setActiveTab]       = useState('map');
  const [showCalendar, setShowCalendar] = useState(false);
  console.log('ExpressDashboard user_id:', user_id);

  // ── Shared date — drives fetches for all tabs ─────────────
  const [selectedDate, setSelectedDate] = useState(getTodayLocal());

  // ── Shared data state ─────────────────────────────────────
  const [deliveries, setDeliveries]               = useState([]);
  const [drivers, setDrivers]                     = useState([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);

  // ── Fetch deliveries for a given date (Option A — one request, shared) ──
  const fetchDeliveries = useCallback(async (date) => {
    setLoadingDeliveries(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/express/deliveries`, {
        params: { date_from: date, date_to: date },
      });
      setDeliveries(data);
    } catch (err) {
      console.error('[ExpressDashboard] Error fetching deliveries:', err);
    } finally {
      setLoadingDeliveries(false);
    }
  }, []);

  // ── Fetch drivers once — always current, no date filter ───
  const fetchDrivers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/express/drivers`);
      setDrivers(data);
    } catch (err) {
      console.error('[ExpressDashboard] Error fetching drivers:', err);
    }
  }, []);

  // ── Initial load with today's date ───────────────────────
  useEffect(() => {
    fetchDeliveries(selectedDate);
    fetchDrivers();
  }, []);

  // ── Date change handler — re-fetches deliveries ───────────
  function handleDateChange(newDate) {
    setShowCalendar(false);
    setSelectedDate(newDate);
    fetchDeliveries(newDate);
  }

  // ── Join / leave express_ops socket room ──────────────────
  useEffect(() => {
    socket.emit('join_express_ops');
    return () => socket.emit('leave_express_ops');
  }, []);

  // ── Socket: new delivery — only add if matches selected date ──
  useEffect(() => {
    function onDeliveryCreated(data) {
      if (data.scheduled_date === selectedDate) {
        setDeliveries(prev => [data, ...prev]);
      }
    }
    socket.on('express_delivery_created', onDeliveryCreated);
    return () => socket.off('express_delivery_created', onDeliveryCreated);
  }, [selectedDate]);

  // ── Socket: status updated ────────────────────────────────
  useEffect(() => {
    function onStatusUpdated({ delivery_id, to_status, updated_at }) {
      setDeliveries(prev =>
        prev.map(d => d.id === delivery_id ? { ...d, status: to_status, updated_at } : d)
      );
    }
    socket.on('express_status_updated', onStatusUpdated);
    return () => socket.off('express_status_updated', onStatusUpdated);
  }, []);

  // ── Socket: driver assigned ───────────────────────────────
  useEffect(() => {
    function onDriverAssigned({ delivery_id, driver, updated_at }) {
      setDeliveries(prev =>
        prev.map(d =>
          d.id === delivery_id
            ? { ...d, driver, driver_id: driver?.user_id ?? null, updated_at }
            : d
        )
      );
    }
    socket.on('express_driver_assigned', onDriverAssigned);
    return () => socket.off('express_driver_assigned', onDriverAssigned);
  }, []);

  // ── Socket: driver location ───────────────────────────────
  useEffect(() => {
    function onDriverLocation({ user_id: driverId, latitude, longitude }) {
      setDrivers(prev =>
        prev.map(d => d.user_id === driverId ? { ...d, latitude, longitude } : d)
      );
    }
    socket.on('driver_location_updated', onDriverLocation);
    return () => socket.off('driver_location_updated', onDriverLocation);
  }, []);

  // ── Patch a single delivery in shared state ───────────────
  const updateDelivery = useCallback((updated) => {
    setDeliveries(prev =>
      prev.map(d => d.id === updated.id ? { ...d, ...updated } : d)
    );
  }, []);

  const unassignedCount = deliveries.filter(
    d => !d.driver && d.status === 'pending'
  ).length;

  const tabProps = {
    user_id,
    deliveries,
    setDeliveries,
    drivers,
    setDrivers,
    updateDelivery,
    selectedDate,
    loading: loadingDeliveries,
  };

  return (
    <div className="min-h-screen bg-gray-50 font-montserrat">

      {/* ── Header ── */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center gap-3 flex-wrap">

          {/* Back */}
          <button
            onClick={() => setActiveComponent('OptionsAdmin')}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 shrink-0"
            aria-label="Volver"
          >
            <ArrowLeft size={20} />
          </button>

          {/* Title */}
          <div className="shrink-0">
            <h1 className="text-lg font-bold text-brand-primary leading-tight">
              Servicio Express
            </h1>
            <p className="text-xs text-gray-400">Panel de operaciones</p>
          </div>

          {/* Date selector — fills remaining space, capped width */}
          <div className="flex-1 min-w-[180px] max-w-[260px] relative">
            <DateSelector
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
              showCalendar={showCalendar}
              onToggleCalendar={() => setShowCalendar(v => !v)}
            />
          </div>

          {/* Loading indicator */}
          {loadingDeliveries && (
            <span className="text-xs text-gray-400 shrink-0 animate-pulse">
              Cargando...
            </span>
          )}

          {/* Unassigned badge */}
          {unassignedCount > 0 && (
            <span className="shrink-0 bg-red-100 text-red-700 text-xs font-semibold px-2.5 py-1 rounded-full">
              {unassignedCount} sin conductor
            </span>
          )}
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className="bg-white border-b border-gray-200 px-4">
        <div className="flex gap-1">
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex flex-col items-center gap-0.5 px-4 py-3 text-xs font-medium border-b-2 transition-colors ${
                activeTab === id
                  ? 'border-brand-primary text-brand-primary'
                  : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}
            >
              <Icon size={18} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab content ── */}
      <div className="p-4">
        {activeTab === 'map'     && <ExpressMapTab     {...tabProps} />}
        {activeTab === 'list'    && <ExpressListTab    {...tabProps} />}
        {activeTab === 'drivers' && <ExpressDriversTab {...tabProps} />}
        {activeTab === 'pricing' && <ExpressPricingTab {...tabProps} />}
      </div>
    </div>
  );
}